/**
 * useReceiptSignedUrl - Hook utilitário para gerar URLs assinadas do bucket 'anexos'
 *
 * Centraliza a criação de signed URLs (1h) para comprovantes de baixa manual
 * armazenados em `financeiro_parcelas.paid_receipt_path`. Reutilizável por
 * qualquer tela do módulo financeiro.
 *
 * RELEASE: 2026-06-09 v1.0.0 — Criação do helper para preview de comprovantes
 *          de pagamento manual (modal de detalhes da cobrança).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ReceiptSignedUrl');

/** Tempo de vida do link assinado (1 hora) */
const SIGNED_URL_TTL_SECONDS = 60 * 60;

interface UseReceiptSignedUrlResult {
  /** URL assinada pronta para uso em <img>, <iframe> ou lightbox */
  url: string | null;
  /** Está carregando a URL */
  isLoading: boolean;
  /** Erro (se houver) ao gerar a URL */
  error: Error | null;
}

/**
 * Gera (e cacheia) um signed URL para um caminho no bucket `anexos`.
 *
 * @param path Caminho completo dentro do bucket. Passe `null` para desabilitar.
 */
export function useReceiptSignedUrl(path: string | null | undefined): UseReceiptSignedUrlResult {
  const query = useQuery({
    queryKey: ['receipt-signed-url', path],
    enabled: !!path,
    // Mantém em cache por ~50 min para evitar re-geração desnecessária dentro do TTL
    staleTime: 50 * 60 * 1000,
    gcTime: 55 * 60 * 1000,
    queryFn: async () => {
      if (!path) return null;
      const { data, error } = await supabase.storage
        .from('anexos')
        .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);

      if (error) {
        logger.error('Falha ao gerar signed URL do comprovante', {
          path,
          error: error.message,
        });
        throw error;
      }
      return data?.signedUrl ?? null;
    },
  });

  return {
    url: query.data ?? null,
    isLoading: query.isFetching,
    error: (query.error as Error) ?? null,
  };
}
